import { useMemo } from "react";
import { Database } from "../lib/database";
import { SavedListWithDetails } from "../store/savedListStore";

type List = Database["public"]["Tables"]["lists"]["Row"];

export type SortOption =
  | "date-newest"
  | "date-oldest"
  | "title-asc"
  | "title-desc";

interface UseListSearchSortParams {
  lists: List[];
  savedLists: SavedListWithDetails[];
  searchQuery: string;
  sortOption: SortOption;
}

interface UseListSearchSortResult {
  filteredOwnLists: List[];
  filteredSavedLists: SavedListWithDetails[];
}

const matchesQuery = (title: string, description: string | null, query: string): boolean => {
  const lowerQuery = query.toLowerCase();
  if (title.toLowerCase().includes(lowerQuery)) return true;
  if (description && description.toLowerCase().includes(lowerQuery)) return true;
  return false;
};

const compareByTitle = (a: string, b: string, asc: boolean): number => {
  const result = a.localeCompare(b);
  return asc ? result : -result;
};

const compareByDate = (
  a: string | null,
  b: string | null,
  newest: boolean
): number => {
  const dateA = a ? new Date(a).getTime() : 0;
  const dateB = b ? new Date(b).getTime() : 0;
  return newest ? dateB - dateA : dateA - dateB;
};

export const useListSearchSort = ({
  lists,
  savedLists,
  searchQuery,
  sortOption,
}: UseListSearchSortParams): UseListSearchSortResult => {
  const filteredOwnLists = useMemo(() => {
    const filtered = searchQuery.trim()
      ? lists.filter((list) => matchesQuery(list.title, list.description, searchQuery))
      : lists;

    return [...filtered].sort((a, b) => {
      switch (sortOption) {
        case "date-newest":
          return compareByDate(a.created_at, b.created_at, true);
        case "date-oldest":
          return compareByDate(a.created_at, b.created_at, false);
        case "title-asc":
          return compareByTitle(a.title, b.title, true);
        case "title-desc":
          return compareByTitle(a.title, b.title, false);
        default:
          return 0;
      }
    });
  }, [lists, searchQuery, sortOption]);

  const filteredSavedLists = useMemo(() => {
    const filtered = searchQuery.trim()
      ? savedLists.filter((saved) =>
          matchesQuery(saved.list.title, saved.list.description, searchQuery)
        )
      : savedLists;

    return [...filtered].sort((a, b) => {
      switch (sortOption) {
        case "date-newest":
          return compareByDate(a.created_at, b.created_at, true);
        case "date-oldest":
          return compareByDate(a.created_at, b.created_at, false);
        case "title-asc":
          return compareByTitle(a.list.title, b.list.title, true);
        case "title-desc":
          return compareByTitle(a.list.title, b.list.title, false);
        default:
          return 0;
      }
    });
  }, [savedLists, searchQuery, sortOption]);

  return { filteredOwnLists, filteredSavedLists };
};
